import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import Header from './header';
import Footer from './footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCheck, faClock, faUsers, faEye, faCalendarAlt, faTimes, faChartLine, faHistory, faArrowLeft, faTrophy, faCrown, faMedal } from '@fortawesome/free-solid-svg-icons';
import { useCustomAlert } from './CustomAlert';
import { timeAgo } from '../utils/functions';
import './GroupGoals.css';

const DepartmentGoals = () => {
    const { user } = useContext(MyContext);
    const { groupId, departmentId } = useParams();
    const navigate = useNavigate();
    const { showAlert, showConfirm, AlertComponent } = useCustomAlert();
    
    const [department, setDepartment] = useState(null);
    const [goals, setGoals] = useState([]);
    const [topPerformers, setTopPerformers] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [userRole, setUserRole] = useState('');
    const [isDepartmentLeader, setIsDepartmentLeader] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState(null);
    const [completionNote, setCompletionNote] = useState('');
    const [progressReports, setProgressReports] = useState([]);
    const [expandedDescriptions, setExpandedDescriptions] = useState({});
    
    // Form states for creating goals
    const [newGoal, setNewGoal] = useState({
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        frequency: 'one-off'
    });

    // Fetch department information
    const fetchDepartment = async () => {
        try {
            setLoading(true);
            setError('');
            
            const response = await fetch(`${backendUrl}/get_department_detail.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    department_id: departmentId,
                    user_id: user.user_id
                })
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.success) {
                setDepartment(data.data.department);
                setIsDepartmentLeader(data.data.is_department_leader);
            } else {
                setError(data.messages || 'Failed to fetch department information');
            }
        } catch (err) {
            console.error('Error fetching department:', err);
            setError(`Failed to fetch department information: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Fetch department goals
    const fetchGoals = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_department_goals.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    department_id: departmentId, 
                    user_id: user.user_id 
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setGoals(data.data.goals || []);
                setUserRole(data.data.user_role || 'member');
            } else {
                setError(data.message);
            }
        } catch (err) {
            console.error('Error fetching goals:', err);
            setError('Failed to fetch goals');
        } finally {
            setLoading(false);
        }
    };

    // Fetch top performers
    const fetchTopPerformers = async () => {
        try {
            const response = await fetch(`${backendUrl}/get_department_top_performers.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    department_id: departmentId,
                    user_id: user.user_id,
                    limit: 5
                })
            });
            const data = await response.json();
            if (data.success) {
                setTopPerformers(data.data);
            }
        } catch (err) {
            console.error('Error loading top performers:', err);
        }
    };

    // Fetch progress reports (department leader only)
    const fetchProgressReports = async (goal) => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/get_department_goal_progress_reports.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    goal_id: goal.id,
                    user_id: user.user_id
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setProgressReports(data.data);
                setSelectedGoal(goal);
                setShowProgressModal(true);
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to fetch progress reports');
        } finally {
            setLoading(false);
        }
    };

    // Create a new goal
    const createGoal = async () => {
        if (!newGoal.title.trim() || !newGoal.description.trim() || !newGoal.start_date) {
            setError('Title, description, and start date are required');
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/create_department_goal.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    department_id: departmentId,
                    created_by: user.user_id,
                    title: newGoal.title.trim(),
                    description: newGoal.description.trim(),
                    start_date: newGoal.start_date,
                    end_date: newGoal.end_date || null,
                    frequency: newGoal.frequency
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setShowCreateModal(false);
                setNewGoal({ title: '', description: '', start_date: '', end_date: '', frequency: 'one-off' });
                await fetchGoals(); // Refresh goals list
                showAlert('Goal created successfully!', 'success');
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to create goal');
        } finally {
            setLoading(false);
        }
    };

    // Mark goal as complete
    const markGoalComplete = async () => {
        if (!completionNote.trim()) {
            setError('Please add a note when marking goal as complete');
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/mark_department_goal_complete.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    goal_id: selectedGoal.id,
                    user_id: user.user_id,
                    note: completionNote.trim()
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setShowCompletionModal(false);
                setCompletionNote('');
                setSelectedGoal(null);
                await fetchGoals(); // Refresh goals list
                showAlert('Goal marked as complete successfully!', 'success');
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to mark goal as complete');
        } finally {
            setLoading(false);
        }
    };

    // Format date for display using the existing timeAgo function
    const formatDate = (dateString) => {
        return timeAgo(dateString);
    };

    // Format date range for display
    const formatDateRange = (startDate, endDate) => {
        if (!startDate) return 'No start date';
        if (!endDate) return `Started ${timeAgo(startDate)} (Ongoing)`;
        
        const startFormatted = timeAgo(startDate);
        const endFormatted = timeAgo(endDate);
        
        // Check if goal is expired using Safari-compatible date parsing
        const isoEndDate = endDate.replace(' ', 'T');
        const end = new Date(isoEndDate);
        const now = new Date();
        
        if (isNaN(end.getTime())) return 'Invalid date range';
        
        if (end < now) {
            return `${startFormatted} - ${endFormatted} (Ended)`;
        } else {
            return `${startFormatted} - ${endFormatted} (Active)`;
        }
    };

    // Check if goal is expired
    const isGoalExpired = (endDate) => {
        if (!endDate) return false;
        
        // Convert to ISO format for Safari compatibility
        const isoDateString = endDate.replace(' ', 'T');
        const end = new Date(isoDateString);
        
        if (isNaN(end.getTime())) return false;
        
        const now = new Date();
        return end < now;
    };

    // Get frequency display
    const getFrequencyDisplay = (frequency) => {
        const frequencyMap = {
            'one-off': 'One-time',
            'daily': 'Daily',
            'weekly': 'Weekly',
            'monthly': 'Monthly'
        };
        return frequencyMap[frequency] || frequency;
    };

    // Utility function to truncate text to approximately 2 lines
    const truncateText = (text, maxLength = 120) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength).trim() + '...';
    };

    // Toggle description expansion
    const toggleDescription = (goalId) => {
        setExpandedDescriptions(prev => ({
            ...prev,
            [goalId]: !prev[goalId]
        }));
    };

    useEffect(() => {
        if (user && user.user_id && departmentId) {
            fetchDepartment();
            fetchGoals();
            fetchTopPerformers();
        }
    }, [user, departmentId]);

    const renderCreateGoalModal = () => (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h5>Create New Department Goal</h5>
                    <button className="close-btn" onClick={() => setShowCreateModal(false)}>×</button>
                </div>
                <div className="modal-body">
                    <div className="form-group">
                        <label>Title *</label>
                        <input
                            type="text"
                            value={newGoal.title}
                            onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
                            placeholder="Enter goal title"
                            className="form-control"
                        />
                    </div>
                    <div className="form-group">
                        <label>Description *</label>
                        <textarea
                            value={newGoal.description}
                            onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
                            placeholder="Enter goal description"
                            className="form-control"
                            rows="4"
                        />
                    </div>
                    <div className="form-group">
                        <label>Start Date *</label>
                        <input
                            type="datetime-local"
                            value={newGoal.start_date}
                            onChange={(e) => setNewGoal({...newGoal, start_date: e.target.value})}
                            className="form-control"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>End Date (Optional)</label>
                        <input
                            type="datetime-local"
                            value={newGoal.end_date}
                            onChange={(e) => setNewGoal({...newGoal, end_date: e.target.value})}
                            className="form-control"
                        />
                        <small className="form-text text-muted">Leave empty for ongoing goals</small>
                    </div>
                    <div className="form-group">
                        <label>Frequency</label>
                        <select
                            value={newGoal.frequency}
                            onChange={(e) => setNewGoal({...newGoal, frequency: e.target.value})}
                            className="form-control"
                        >
                            <option value="one-off">One-time</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                        </select>
                        <small className="form-text text-muted">
                            {newGoal.frequency === 'one-off' ? 'Goal can be completed once' : 
                             newGoal.frequency === 'daily' ? 'Goal resets every day' :
                             newGoal.frequency === 'weekly' ? 'Goal resets every week' :
                             'Goal resets every month'}
                        </small>
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                        Cancel
                    </button>
                    <button className="btn btn-primary" onClick={createGoal} disabled={loading}>
                        {loading ? 'Creating...' : 'Create Goal'}
                    </button>
                </div>
            </div>
        </div>
    );

    const renderCompletionModal = () => (
        <div className="modal-overlay" onClick={() => setShowCompletionModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h5>Mark Goal as Complete</h5>
                    <button className="close-btn" onClick={() => setShowCompletionModal(false)}>×</button>
                </div>
                <div className="modal-body">
                    <div className="goal-preview">
                        <h6>{selectedGoal?.title}</h6>
                        <p>{selectedGoal?.description}</p>
                    </div>
                    <div className="form-group">
                        <label>Completion Note *</label>
                        <textarea
                            value={completionNote}
                            onChange={(e) => setCompletionNote(e.target.value)}
                            placeholder="Add a note about how you completed this goal..."
                            className="form-control"
                            rows="4"
                        />
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowCompletionModal(false)}>
                        Cancel
                    </button>
                    <button className="btn btn-success" onClick={markGoalComplete} disabled={loading}>
                        {loading ? 'Marking...' : 'Mark Complete'}
                    </button>
                </div>
            </div>
        </div>
    );

    const renderProgressModal = () => (
        <div className="modal-overlay" onClick={() => setShowProgressModal(false)}>
            <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h5>Progress Reports - {selectedGoal?.title}</h5>
                    <button className="close-btn" onClick={() => setShowProgressModal(false)}>×</button>
                </div>
                <div className="modal-body">
                    <div className="goal-info">
                        <h6>{progressReports.goal?.title}</h6>
                        <div className="goal-meta">
                            <span className="frequency-badge">
                                <FontAwesomeIcon icon={faClock} />
                                {getFrequencyDisplay(progressReports.goal?.frequency)}
                            </span>
                            <span className="date-range">
                                <FontAwesomeIcon icon={faCalendarAlt} />
                                {formatDateRange(progressReports.goal?.start_date, progressReports.goal?.end_date)}
                            </span>
                        </div>
                    </div>
                    
                    <div className="recent-progress-section">
                        <h6>Recent Progress</h6>
                        <div className="progress-cards-container">
                            {progressReports.current_progress && (
                                <div className="progress-card current">
                                    <div className="progress-header">
                                        <span className="period-label">Current Period</span>
                                        <span className="completion-rate">
                                            {progressReports.current_progress.completion_percentage}%
                                        </span>
                                    </div>
                                    <div className="progress-bar">
                                        <div 
                                            className="progress-fill" 
                                            style={{ width: `${progressReports.current_progress.completion_percentage}%` }}
                                        ></div>
                                    </div>
                                    <div className="progress-stats">
                                        <span>{progressReports.current_progress.completed_count} / {progressReports.current_progress.total_members} completed</span>
                                    </div>
                                    <div className="period-dates">
                                        {progressReports.current_progress.interval_display || 
                                         `${formatDate(progressReports.current_progress.interval_start_date)} - ${formatDate(progressReports.current_progress.interval_end_date)}`}
                                    </div>
                                </div>
                            )}
                            
                            {progressReports.previous_progress && (
                                <div className="progress-card previous">
                                    <div className="progress-header">
                                        <span className="period-label">Previous Period</span>
                                        <span className="completion-rate">
                                            {progressReports.previous_progress.completion_percentage}%
                                        </span>
                                    </div>
                                    <div className="progress-bar">
                                        <div 
                                            className="progress-fill" 
                                            style={{ width: `${progressReports.previous_progress.completion_percentage}%` }}
                                        ></div>
                                    </div>
                                    <div className="progress-stats">
                                        <span>{progressReports.previous_progress.completed_count} / {progressReports.previous_progress.total_members} completed</span>
                                    </div>
                                    <div className="period-dates">
                                        {progressReports.previous_progress.interval_display || 
                                         `${formatDate(progressReports.previous_progress.interval_start_date)} - ${formatDate(progressReports.previous_progress.interval_end_date)}`}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <div className="historical-reports-section">
                        <div className="section-header">
                            <h6>Historical Reports</h6>
                        </div>
                        {progressReports.reports?.length > 0 ? (
                            <div className="reports-list">
                                {progressReports.reports.slice(0, 3).map((report, index) => (
                                    <div key={index} className="progress-card historical">
                                        <div className="progress-header">
                                            <span className="period-label">
                                                {formatDate(report.interval_start_date)} - {formatDate(report.interval_end_date)}
                                            </span>
                                            <span className="completion-rate">
                                                {report.completion_percentage}%
                                            </span>
                                        </div>
                                        <div className="progress-bar">
                                            <div 
                                                className="progress-fill" 
                                                style={{ width: `${report.completion_percentage}%` }}
                                            ></div>
                                        </div>
                                        <div className="progress-stats">
                                            <span>{report.completed_count} / {report.total_members} completed</span>
                                        </div>
                                    </div>
                                ))}
                                {progressReports.reports.length > 3 && (
                                    <div className="more-reports">
                                        <p className="text-muted">+{progressReports.reports.length - 3} more reports available</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="no-reports">
                                <FontAwesomeIcon icon={faHistory} size="2x" className="text-muted mb-2" />
                                <p>No historical reports available yet</p>
                                <small className="text-muted">Historical reports will appear here as the goal progresses</small>
                            </div>
                        )}
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowProgressModal(false)}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );

    if (!user || !user.user_id) {
        return <div className="container-fluid p-3">
            <div className="alert alert-warning" role="alert">
                <h5>Authentication Required</h5>
                <p>Please log in to access department goals.</p>
            </div>
        </div>;
    }

    if (loading && !department) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <div className="ms-3">Loading department goals...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container-fluid p-3">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="h-100 d-flex flex-column">
            <Header />
            <div className="flex-grow-1" style={{ marginTop: '80px', marginBottom: '60px' }}>
                <div className="container-fluid p-3">
                    <div className="row">
                        <div className="col-12">
                            <div className="d-flex align-items-center mb-3">
                                <div>
                                    <h2 className="mb-0">🎯 Department Goals - {department?.name}</h2>
                                    <p className="text-muted mb-0">Track and complete department objectives together</p>
                                </div>
                                <div className="ms-auto d-flex gap-2">
                                    {isDepartmentLeader && (
                                        <button 
                                            className="btn btn-primary"
                                            onClick={() => setShowCreateModal(true)}
                                        >
                                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                                            Create Goal
                                        </button>
                                    )}
                                </div>
                            </div>

                            {error && <div className="alert alert-danger">{error}</div>}

                            {/* Top Performers Section */}
                            {topPerformers && topPerformers.all_time_performers.length > 0 && (
                                <div className="row mb-4">
                                    <div className="col-12">
                                        <div className="card">
                                            <div className="card-body">
                                                <h5 className="card-title">
                                                    <FontAwesomeIcon icon={faTrophy} className="me-2" />
                                                    Top Performers
                                                </h5>
                                                <div className="row">
                                                    {topPerformers.all_time_performers.slice(0, 3).map((performer, index) => (
                                                        <div key={performer.user_id} className="col-md-4 mb-3">
                                                            <div className="d-flex align-items-center">
                                                                <div className="me-3">
                                                                    {index === 0 && <FontAwesomeIcon icon={faCrown} className="text-warning" size="2x" />}
                                                                    {index === 1 && <FontAwesomeIcon icon={faMedal} className="text-secondary" size="2x" />}
                                                                    {index === 2 && <FontAwesomeIcon icon={faMedal} className="text-warning" size="2x" />}
                                                                </div>
                                                                <div>
                                                                    <h6 className="mb-0">{performer.first_name} {performer.last_name}</h6>
                                                                    <small className="text-muted">{performer.total_completions} completions</small>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="goals-container">
                                {goals.length > 0 ? (
                                    <div className="row">
                                        {goals.map(goal => (
                                            <div key={goal.id} className="col-md-6 col-lg-4 mb-4">
                                                <div className="goal-card">
                                                    <div className="goal-header">
                                                        <h5 className="goal-title">{goal.title}</h5>
                                                        <div className="goal-meta">
                                                            <div className="frequency-badge">
                                                                <FontAwesomeIcon icon={faClock} />
                                                                {getFrequencyDisplay(goal.frequency)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="goal-description">
                                                        <p className="goal-description-text">
                                                            {expandedDescriptions[goal.id] ? goal.description : truncateText(goal.description)}
                                                        </p>
                                                        {goal.description && goal.description.length > 120 && (
                                                            <button 
                                                                className="read-more-btn"
                                                                onClick={() => toggleDescription(goal.id)}
                                                            >
                                                                {expandedDescriptions[goal.id] ? 'Read Less' : 'Read More'}
                                                            </button>
                                                        )}
                                                        <div className="goal-dates">
                                                            <small className="text-muted">
                                                                {formatDateRange(goal.start_date, goal.end_date)}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="goal-stats">
                                                        <div className="completion-bar">
                                                            <div 
                                                                className="completion-fill" 
                                                                style={{ width: `${goal.completion_percentage}%` }}
                                                            ></div>
                                                        </div>
                                                        <div className="stats-text">
                                                            {goal.completion_count} / {goal.total_members} completed
                                                            ({goal.completion_percentage}%)
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="goal-actions">
                                                        <div className="d-flex gap-2 align-items-center">
                                                            {!goal.user_has_completed && !isGoalExpired(goal.end_date) ? (
                                                                <button 
                                                                    className="btn btn-success btn-sm"
                                                                    onClick={() => {
                                                                        setSelectedGoal(goal);
                                                                        setShowCompletionModal(true);
                                                                    }}
                                                                >
                                                                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                                                                    Mark Complete
                                                                </button>
                                                            ) : goal.user_has_completed ? (
                                                                <span className="completed-badge">
                                                                    <FontAwesomeIcon icon={faCheck} />
                                                                    Completed
                                                                </span>
                                                            ) : (
                                                                <span className="expired-badge">
                                                                    <FontAwesomeIcon icon={faClock} />
                                                                    Expired
                                                                </span>
                                                            )}
                                                            
                                                            {isDepartmentLeader && goal.frequency !== 'one-off' && (
                                                                <button 
                                                                    className="btn btn-outline-info btn-sm"
                                                                    onClick={() => fetchProgressReports(goal)}
                                                                    title="View Progress Reports"
                                                                >
                                                                    <FontAwesomeIcon icon={faChartLine} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <FontAwesomeIcon icon={faCheck} size="3x" className="text-muted mb-3" />
                                        <h5 className="text-muted">No Goals Yet</h5>
                                        <p className="text-muted">
                                            {isDepartmentLeader 
                                                ? 'Create the first goal to get started!' 
                                                : 'No goals have been created for this department yet.'
                                            }
                                        </p>
                                        {isDepartmentLeader && (
                                            <button 
                                                className="btn btn-primary"
                                                onClick={() => setShowCreateModal(true)}
                                            >
                                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                Create First Goal
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
            
            {/* Modals */}
            {showCreateModal && renderCreateGoalModal()}
            {showCompletionModal && renderCompletionModal()}
            {showProgressModal && renderProgressModal()}
            
            {/* Custom Alert Component */}
            <AlertComponent />
        </div>
    );
};

export default DepartmentGoals;
